/**
 * MoneylineTab Component
 *
 * Displays moneyline (h2h) market data including:
 * - Volume chart showing ticket % vs money % over time
 * - Current moneyline odds from all bookmakers
 * - Line movement analysis
 * - Sharp money indicator
 */

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import type { BettingVolumeResponse } from '../../../api/client';

interface MarketOutcome {
  name: string;
  bestOdds: number;
  bestBook: string;
  avgOdds: number;
  impliedProb: number;
  point?: number | null;
  bookmakers: Array<{
    name: string;
    price: number;
    isSharp: boolean;
  }>;
}

interface MoneylineTabProps {
  bettingVolumeData: BettingVolumeResponse | null | undefined;
  homeTeam: string;
  awayTeam: string;
  lineMovement: {
    openingLine: { home: number; away: number } | null;
    currentLine: { home: number; away: number } | null;
    movement: { home: number; away: number } | null;
  } | null;
}

// Format decimal to American odds
const formatAmericanOdds = (price: number): string => {
  if (price >= 2) {
    return `+${Math.round((price - 1) * 100)}`;
  }
  return `-${Math.round(100 / (price - 1))}`;
};

// Get short team name
const getShortName = (name: string): string => {
  const parts = name.split(' ');
  return parts[parts.length - 1];
};

export default function MoneylineTab({
  bettingVolumeData,
  homeTeam,
  awayTeam,
  lineMovement,
}: MoneylineTabProps) {
  // Find h2h market data
  const moneylineMarket = bettingVolumeData?.markets.find(m => m.marketType === 'h2h');
  const publicBetting = bettingVolumeData?.publicBetting;
  const summary = bettingVolumeData?.summary;

  // Calculate RLM indicator (money % - ticket %)
  const rlmIndicator = publicBetting
    ? (publicBetting.moneyPctHome - publicBetting.ticketPctHome)
    : null;

  return (
    <div className="space-y-6">
      {/* Volume Distribution Chart */}
      {publicBetting && (
        <div className="card">
          <div className="mb-4">
            <h2 className="text-xl font-bold">MONEYLINE VOLUME</h2>
            <p className="text-sm text-gray-400 mt-1">
              Public betting distribution: Tickets vs Money
            </p>
          </div>

          {/* Visual Comparison Chart */}
          <div className="bg-surface-50 rounded-lg p-6 border border-gray-800">
            <div className="grid grid-cols-2 gap-8">
              {/* Home Team */}
              <div>
                <div className="text-center mb-4">
                  <span className="text-lg font-bold text-blue-400">{getShortName(homeTeam)}</span>
                </div>

                {/* Ticket % Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Tickets</span>
                    <span className="font-mono">{publicBetting.ticketPctHome.toFixed(1)}%</span>
                  </div>
                  <div className="h-6 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500/60 transition-all rounded-full"
                      style={{ width: `${publicBetting.ticketPctHome}%` }}
                    />
                  </div>
                </div>

                {/* Money % Bar */}
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Money</span>
                    <span className="font-mono">{publicBetting.moneyPctHome.toFixed(1)}%</span>
                  </div>
                  <div className="h-6 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all rounded-full"
                      style={{ width: `${publicBetting.moneyPctHome}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Away Team */}
              <div>
                <div className="text-center mb-4">
                  <span className="text-lg font-bold text-orange-400">{getShortName(awayTeam)}</span>
                </div>

                {/* Ticket % Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Tickets</span>
                    <span className="font-mono">{publicBetting.ticketPctAway.toFixed(1)}%</span>
                  </div>
                  <div className="h-6 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500/60 transition-all rounded-full"
                      style={{ width: `${publicBetting.ticketPctAway}%` }}
                    />
                  </div>
                </div>

                {/* Money % Bar */}
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Money</span>
                    <span className="font-mono">{publicBetting.moneyPctAway.toFixed(1)}%</span>
                  </div>
                  <div className="h-6 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500 transition-all rounded-full"
                      style={{ width: `${publicBetting.moneyPctAway}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RLM Indicator */}
            {rlmIndicator !== null && (
              <div className="mt-6 pt-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-300">Reverse Line Movement Indicator</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Money% - Ticket% | Positive = sharp action on home
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-3xl font-bold font-mono ${
                      Math.abs(rlmIndicator) >= 10
                        ? rlmIndicator > 0 ? 'text-blue-400' : 'text-orange-400'
                        : 'text-gray-400'
                    }`}>
                      {rlmIndicator > 0 ? '+' : ''}{rlmIndicator.toFixed(1)}%
                    </span>
                    {Math.abs(rlmIndicator) >= 10 && (
                      <div className="flex items-center gap-2 justify-end mt-1">
                        <span className="text-yellow-400 text-sm">RLM Signal</span>
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                          Sharp on {rlmIndicator > 0 ? getShortName(homeTeam) : getShortName(awayTeam)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Stacked Area Chart for Volume */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Volume Breakdown
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={[
                    {
                      category: 'Tickets',
                      [getShortName(homeTeam)]: publicBetting.ticketPctHome,
                      [getShortName(awayTeam)]: publicBetting.ticketPctAway,
                    },
                    {
                      category: 'Money',
                      [getShortName(homeTeam)]: publicBetting.moneyPctHome,
                      [getShortName(awayTeam)]: publicBetting.moneyPctAway,
                    },
                  ]}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="category" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`${value.toFixed(1)}%`]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey={getShortName(homeTeam)}
                    stackId="1"
                    stroke="#3B82F6"
                    fill="#3B82F6"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey={getShortName(awayTeam)}
                    stackId="1"
                    stroke="#F97316"
                    fill="#F97316"
                    fillOpacity={0.6}
                  />
                  <ReferenceLine y={50} stroke="#6B7280" strokeDasharray="3 3" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Line Movement */}
      {lineMovement?.openingLine && lineMovement?.currentLine && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">MONEYLINE MOVEMENT</h2>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-surface-50 rounded-lg p-4 border border-gray-800">
              <div className="text-xs text-gray-500 mb-2 uppercase">Opening</div>
              <div className="flex justify-center gap-4">
                <div>
                  <span className="text-xs text-blue-400">{getShortName(homeTeam)}</span>
                  <div className="text-lg font-mono font-bold text-blue-400">
                    {formatAmericanOdds(lineMovement.openingLine.home)}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-orange-400">{getShortName(awayTeam)}</span>
                  <div className="text-lg font-mono font-bold text-orange-400">
                    {formatAmericanOdds(lineMovement.openingLine.away)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-50 rounded-lg p-4 border border-purple-500/30">
              <div className="text-xs text-gray-500 mb-2 uppercase">Current</div>
              <div className="flex justify-center gap-4">
                <div>
                  <span className="text-xs text-blue-400">{getShortName(homeTeam)}</span>
                  <div className="text-lg font-mono font-bold text-blue-400">
                    {formatAmericanOdds(lineMovement.currentLine.home)}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-orange-400">{getShortName(awayTeam)}</span>
                  <div className="text-lg font-mono font-bold text-orange-400">
                    {formatAmericanOdds(lineMovement.currentLine.away)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-50 rounded-lg p-4 border border-gray-800">
              <div className="text-xs text-gray-500 mb-2 uppercase">Movement</div>
              {lineMovement.movement && (
                <div className="flex justify-center gap-4">
                  <div>
                    <span className="text-xs text-blue-400">{getShortName(homeTeam)}</span>
                    <div className={`text-lg font-mono font-bold ${
                      lineMovement.movement.home > 0 ? 'text-green-400' :
                      lineMovement.movement.home < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {lineMovement.movement.home > 0 ? '+' : ''}{lineMovement.movement.home.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-orange-400">{getShortName(awayTeam)}</span>
                    <div className={`text-lg font-mono font-bold ${
                      lineMovement.movement.away > 0 ? 'text-green-400' :
                      lineMovement.movement.away < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {lineMovement.movement.away > 0 ? '+' : ''}{lineMovement.movement.away.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bookmaker Odds Table */}
      {moneylineMarket && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">BOOKMAKER MONEYLINE ODDS</h2>
          <p className="text-sm text-gray-400 mb-4">
            {moneylineMarket.bookmakerCount} bookmakers offering moneyline
          </p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-700">
                  <th className="pb-3 pr-4">Bookmaker</th>
                  <th className="pb-3 px-4 text-center">{getShortName(homeTeam)}</th>
                  <th className="pb-3 px-4 text-center">{getShortName(awayTeam)}</th>
                  <th className="pb-3 pl-4">Type</th>
                </tr>
              </thead>
              <tbody>
                {/* Group outcomes by bookmaker */}
                {(() => {
                  const bookmakerOdds = new Map<string, { home?: MarketOutcome['bookmakers'][0], away?: MarketOutcome['bookmakers'][0], isSharp: boolean }>();

                  moneylineMarket.outcomes.forEach(outcome => {
                    const isHome = outcome.name.toLowerCase().includes(homeTeam.split(' ').pop()?.toLowerCase() || '');
                    outcome.bookmakers.forEach(bookie => {
                      if (!bookmakerOdds.has(bookie.name)) {
                        bookmakerOdds.set(bookie.name, { isSharp: bookie.isSharp });
                      }
                      const entry = bookmakerOdds.get(bookie.name)!;
                      if (isHome) {
                        entry.home = bookie;
                      } else {
                        entry.away = bookie;
                      }
                    });
                  });

                  return Array.from(bookmakerOdds.entries())
                    .sort((a, b) => (b[1].isSharp ? 1 : 0) - (a[1].isSharp ? 1 : 0))
                    .map(([name, odds]) => (
                      <tr key={name} className="border-b border-gray-800 hover:bg-surface-50">
                        <td className="py-3 pr-4 font-medium">{name}</td>
                        <td className="py-3 px-4 text-center">
                          {odds.home ? (
                            <span className="font-mono text-blue-400">
                              {formatAmericanOdds(odds.home.price)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {odds.away ? (
                            <span className="font-mono text-orange-400">
                              {formatAmericanOdds(odds.away.price)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-3 pl-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            odds.isSharp
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-gray-700/50 text-gray-400'
                          }`}>
                            {odds.isSharp ? 'Sharp' : 'Square'}
                          </span>
                        </td>
                      </tr>
                    ));
                })()}
              </tbody>
            </table>
          </div>

          {/* Sharp vs Square Summary */}
          {summary?.sharpVsSquareDivergence && (
            <div className="mt-4 p-4 bg-surface-50 rounded-lg border border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Sharp vs Square Divergence</span>
                <div className="flex gap-4">
                  <span className="text-blue-400 font-mono">
                    {getShortName(homeTeam)}: {summary.sharpVsSquareDivergence.home > 0 ? '+' : ''}{summary.sharpVsSquareDivergence.home.toFixed(3)}
                  </span>
                  <span className="text-orange-400 font-mono">
                    {getShortName(awayTeam)}: {summary.sharpVsSquareDivergence.away > 0 ? '+' : ''}{summary.sharpVsSquareDivergence.away.toFixed(3)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No data message */}
      {!publicBetting && !moneylineMarket && (
        <div className="card text-center py-12">
          <p className="text-gray-400">No moneyline data available</p>
          <p className="text-gray-500 text-sm mt-2">
            Moneyline data will appear when odds are collected from bookmakers
          </p>
        </div>
      )}
    </div>
  );
}
